import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, PageHeader, Btn, Badge, Modal, Select } from '../components/Layout';
import api from '../utils/api';

// ── Status color helper ───────────────────────────────────────────────────────
const statusColor = s => {
  if (s === 'ringing' || s === 'initiated') return 'var(--amber)';
  if (s === 'in_progress' || s === 'connected') return 'var(--green)';
  if (s === 'completed' || s === 'ended') return 'var(--muted)';
  if (s === 'failed' || s === 'error') return 'var(--red)';
  return 'var(--accent)';
};

const fmtDuration = secs => {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// ── Script preview (read-only display) ───────────────────────────────────────
const scriptPreview = (lead, company, mode, objective) => {
  if (!lead) return '';
  const first = lead.name?.split(' ')[0] || 'there';
  let opening = '';
  if (objective === 'qualify')      opening = `Hi ${first}, this is a quick call from our team — do you have about 60 seconds?`;
  else if (objective === 'book')    opening = `Hi ${first}, following up on our email — wanted to check if you'd be open to a quick 15-minute call this week?`;
  else if (objective === 'availability') opening = `Hi ${first}, calling to check if you're currently available for new opportunities — do you have a moment?`;
  else                              opening = `Hi ${first}, just following up — did you get a chance to look at our email?`;

  let system = '';
  if (company === 'Maven Group Global' && mode === 'bdm') {
    system = `[Alex from Maven Group Global] Goal: qualify interest in outsourced software development and book a 15-min discovery call with ${lead.name} (${lead.title || 'decision maker'} at ${lead.firmName || 'their company'}).`;
  } else if (mode === 'recruiting') {
    system = `[Priya from ${company}] Goal: confirm if ${lead.name} (${lead.skill || lead.title || 'consultant'}) is on bench/available, get rate expectation and visa status.`;
  } else {
    system = `[Jordan from Xolosoft LLC] Goal: introduce AI/ML capabilities and explore partnership/project opportunities with ${lead.name} at ${lead.firmName || 'their company'}.`;
  }

  return `OPENING: "${opening}"\n\nSYSTEM CONTEXT: ${system}`;
};

// ── Ringing pulse animation ───────────────────────────────────────────────────
const pulseStyle = {
  display: 'inline-block',
  width: 10, height: 10,
  borderRadius: '50%',
  background: 'var(--amber)',
  animation: 'pulse 1s ease-in-out infinite',
  marginRight: 8,
};

export default function AICalls() {
  const { company, mode } = useOutletContext();

  const [leads,          setLeads]          = useState([]);
  const [callHistory,    setCallHistory]    = useState([]);
  const [stats,          setStats]          = useState({ total: 0, answered: 0, booked: 0 });
  const [selectedLead,   setSelectedLead]   = useState(null);
  const [showModal,      setShowModal]      = useState(false);
  const [callStatus,     setCallStatus]     = useState(null);
  const [activeCallId,   setActiveCallId]   = useState(null);
  const [transcript,     setTranscript]     = useState('');
  const [crmSummary,     setCrmSummary]     = useState('');
  const [objective,      setObjective]      = useState('qualify');
  const [loading,        setLoading]        = useState(false);
  const [historyPage,    setHistoryPage]    = useState(1);
  const [historyTotal,   setHistoryTotal]   = useState(0);
  const [leadSearch,     setLeadSearch]     = useState('');
  const [error,          setError]          = useState('');

  const pollingRef = useRef(null);

  // Load leads + history on mount / company/mode change
  useEffect(() => {
    loadLeads();
    loadCallHistory();
    return () => clearPolling();
  }, [company, mode]); // eslint-disable-line

  useEffect(() => { loadCallHistory(); }, [historyPage]); // eslint-disable-line

  const clearPolling = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  };

  const loadLeads = async () => {
    try {
      const r = await api.get('/leads', { params: { company, type: mode, limit: 100 } });
      setLeads(r.data.leads || r.data || []);
    } catch { /* silent */ }
  };

  const loadCallHistory = async () => {
    try {
      const r = await api.get('/aicalls/history', { params: { company, page: historyPage, limit: 20 } });
      setCallHistory(r.data.calls || []);
      setHistoryTotal(r.data.total || 0);

      // Compute stats from history
      const allCalls = r.data.calls || [];
      const answered = allCalls.filter(c => c.status === 'completed').length;
      const booked   = allCalls.filter(c => /booked|calendly|schedule/i.test(c.outcome || '')).length;
      setStats({ total: r.data.total || 0, answered, booked });
    } catch { /* silent */ }
  };

  const openCallModal = lead => {
    setSelectedLead(lead);
    setObjective('qualify');
    setCallStatus(null);
    setTranscript('');
    setCrmSummary('');
    setActiveCallId(null);
    setError('');
    clearPolling();
    setShowModal(true);
  };

  const closeModal = () => {
    if (callStatus === 'ringing' || callStatus === 'connected') {
      if (!window.confirm('A call is in progress. End call and close?')) return;
      if (activeCallId) endCall();
    }
    setShowModal(false);
    clearPolling();
  };

  const pollCallStatus = async callId => {
    try {
      const r = await api.get(`/aicalls/status/${callId}`);
      const s = r.data.status;
      if (s === 'in_progress') setCallStatus('connected');
      if (s === 'ended' || s === 'completed' || s === 'no_answer' || s === 'busy' || s === 'failed') {
        clearPolling();
        setCallStatus('ended');
        if (r.data.transcript) setTranscript(r.data.transcript);
        loadCallHistory();
      }
    } catch { /* keep polling */ }
  };

  const initiateCall = async () => {
    if (!selectedLead) return;
    setLoading(true);
    setError('');
    try {
      const r = await api.post('/aicalls/initiate', {
        leadId:    selectedLead._id,
        company,
        mode,
        objective,
      });
      setActiveCallId(r.data.callId);
      setCallStatus('ringing');
      pollingRef.current = setInterval(() => pollCallStatus(r.data.callId), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate call');
      setCallStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const endCall = async () => {
    if (!activeCallId) return;
    try {
      await api.delete(`/aicalls/end/${activeCallId}`);
    } catch { /* silent */ }
    clearPolling();
    setCallStatus('ended');
    loadCallHistory();
  };

  // Filter leads by search
  const filteredLeads = leads.filter(l => {
    if (!leadSearch) return true;
    const q = leadSearch.toLowerCase();
    return (l.name || '').toLowerCase().includes(q)
        || (l.firmName || '').toLowerCase().includes(q)
        || (l.email || '').toLowerCase().includes(q);
  });

  return (
    <div style={{ padding: 28 }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: .5; transform: scale(1.4); }
        }
      `}</style>

      <PageHeader
        title="🤖 AI Calls"
        sub={`Automated AI voice calling powered by Vapi.ai — ${company} · ${mode.toUpperCase()}`}
      />

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total AI Calls Made',       value: stats.total   },
          { label: 'Calls Answered',             value: stats.answered },
          { label: 'Meetings Booked from Calls', value: stats.booked  },
        ].map(s => (
          <Card key={s.label}>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)' }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{s.label}</div>
          </Card>
        ))}
      </div>

      {/* Lead picker */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            Select Lead to Call
            <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 400, marginLeft: 8 }}>
              ({filteredLeads.length} leads with phone numbers shown)
            </span>
          </div>
          <input
            placeholder="Search leads..."
            value={leadSearch}
            onChange={e => setLeadSearch(e.target.value)}
            style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 7, padding: '8px 12px', color: 'var(--text)', fontSize: 13,
              outline: 'none', width: 220,
            }}
          />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Name', 'Title', 'Company', 'Phone', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: .5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>No leads found</td></tr>
              )}
              {filteredLeads.map(lead => (
                <tr key={lead._id} style={{ borderBottom: '1px solid var(--border)11' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{lead.name}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--muted)' }}>{lead.title || '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--muted)' }}>{lead.firmName || '—'}</td>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                    {lead.phone || <span style={{ color: 'var(--red)', fontSize: 11 }}>No phone</span>}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <Badge label={lead.status} color={lead.status === 'new' ? 'var(--muted)' : lead.status === 'booked' ? 'var(--green)' : 'var(--accent)'} />
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <Btn
                      small
                      disabled={!lead.phone}
                      onClick={() => openCallModal(lead)}
                    >📞 AI Call</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* AI Call Modal */}
      {showModal && selectedLead && (
        <Modal title="🤖 Initiate AI Call" onClose={closeModal} width={620}>
          {/* Lead info */}
          <div style={{ background: 'var(--surface)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{selectedLead.name}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              {selectedLead.title && <span>{selectedLead.title}</span>}
              {selectedLead.firmName && <span> · {selectedLead.firmName}</span>}
            </div>
            <div style={{ fontSize: 13, marginTop: 6, fontFamily: 'monospace', color: 'var(--accent)' }}>
              📞 {selectedLead.phone}
            </div>
          </div>

          {/* Objective selector */}
          {!callStatus && (
            <>
              <Select
                label="Call Objective"
                value={objective}
                onChange={e => setObjective(e.target.value)}
              >
                <option value="qualify">Qualify Interest</option>
                <option value="book">Book Meeting</option>
                <option value="availability">Check Availability</option>
                <option value="followup">Follow-Up</option>
              </Select>

              {/* Script preview */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>Script Preview</label>
                <textarea
                  readOnly
                  value={scriptPreview(selectedLead, company, mode, objective)}
                  rows={5}
                  style={{
                    width: '100%', background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 7, padding: '10px 12px', color: 'var(--muted)', fontSize: 12,
                    fontFamily: 'monospace', resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </>
          )}

          {/* Call status display */}
          {callStatus && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                {callStatus === 'ringing' && <span style={pulseStyle} />}
                {callStatus === 'connected' && <span style={{ ...pulseStyle, background: 'var(--green)', animation: 'none' }} />}
                {(callStatus === 'ended' || callStatus === 'error') && <span style={{ ...pulseStyle, background: 'var(--muted)', animation: 'none' }} />}
                <span style={{
                  fontWeight: 700, fontSize: 14,
                  color: statusColor(callStatus),
                }}>
                  {callStatus === 'ringing'   && 'Ringing...'}
                  {callStatus === 'connected' && 'Call Connected'}
                  {callStatus === 'ended'     && 'Call Ended'}
                  {callStatus === 'error'     && 'Call Failed'}
                </span>
              </div>

              {error && (
                <div style={{ background: 'var(--red)18', border: '1px solid var(--red)33', borderRadius: 7, padding: '10px 14px', color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>
                  {error}
                </div>
              )}

              {/* Transcript */}
              {callStatus === 'ended' && transcript && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Transcript</div>
                  <div style={{
                    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 7,
                    padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text)',
                    maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.6,
                  }}>
                    {transcript}
                  </div>
                </div>
              )}

              {/* CRM Summary */}
              {callStatus === 'ended' && crmSummary && (
                <div style={{ background: 'var(--green)11', border: '1px solid var(--green)33', borderRadius: 7, padding: '10px 14px', fontSize: 13, color: 'var(--text)' }}>
                  <div style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700, marginBottom: 4 }}>AI CRM SUMMARY</div>
                  {crmSummary}
                </div>
              )}

              {callStatus === 'ended' && !transcript && (
                <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                  Transcript will be available after the webhook processes (usually within 30 seconds).
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            {!callStatus && (
              <Btn primary onClick={initiateCall} disabled={loading}>
                {loading ? 'Connecting...' : '📞 Start AI Call'}
              </Btn>
            )}
            {(callStatus === 'ringing' || callStatus === 'connected') && (
              <Btn danger onClick={endCall}>End Call</Btn>
            )}
            {callStatus === 'ended' && (
              <Btn onClick={() => { setCallStatus(null); setTranscript(''); setCrmSummary(''); setActiveCallId(null); }}>
                Make Another Call
              </Btn>
            )}
            <Btn onClick={closeModal}>Close</Btn>
          </div>
        </Modal>
      )}

      {/* Call History */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Call History</div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Lead Name', 'Company', 'Duration', 'Outcome', 'AI Summary', 'Status', 'Date'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: .5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {callHistory.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>No AI call history yet</td></tr>
              )}
              {callHistory.map(call => (
                <tr key={call._id} style={{ borderBottom: '1px solid var(--border)11' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{call.leadName || '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--muted)' }}>{call.company || '—'}</td>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>{fmtDuration(call.duration)}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {call.outcome || '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--muted)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {call.summary || '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <Badge
                      label={call.status || 'unknown'}
                      color={statusColor(call.status)}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--muted)', fontSize: 12 }}>{fmtDate(call.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {historyTotal > 20 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            <Btn small disabled={historyPage <= 1} onClick={() => setHistoryPage(p => p - 1)}>← Prev</Btn>
            <span style={{ padding: '6px 12px', fontSize: 13, color: 'var(--muted)' }}>
              Page {historyPage} of {Math.ceil(historyTotal / 20)}
            </span>
            <Btn small disabled={historyPage >= Math.ceil(historyTotal / 20)} onClick={() => setHistoryPage(p => p + 1)}>Next →</Btn>
          </div>
        )}
      </Card>
    </div>
  );
}
